import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, Loader2, User, Briefcase } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveChatWidget = ({ user, userType = 'customer' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Fetch conversations
  const fetchConversations = async () => {
    if (!user?.id) return;
    
    try {
      const endpoint = userType === 'worker' 
        ? `/messages/conversations/worker/${user.id}`
        : `/messages/conversations/customer/${user.email}`;
      
      const response = await axios.get(`${API}${endpoint}`);
      setConversations(response.data || []);
      
      // Calculate unread count
      const unread = (response.data || []).reduce((sum, conv) => {
        return sum + (userType === 'worker' ? conv.unread_worker : conv.unread_customer) || 0;
      }, 0);
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  };

  // Fetch messages for a conversation
  const fetchMessages = async (conversationId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/messages/${conversationId}`);
      setMessages(response.data || []);
      
      // Mark as read
      await axios.post(`${API}/messages/${conversationId}/read`, {
        user_type: userType
      });
      
      fetchConversations(); // Refresh unread counts
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  // Send a message
  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeConversation) return;
    
    setSending(true);
    try {
      const messageData = {
        conversation_id: activeConversation.id,
        sender_id: user.id || user.email,
        sender_type: userType,
        sender_name: user.name || user.business_name || 'Customer',
        recipient_id: userType === 'worker' ? activeConversation.customer_id : activeConversation.worker_id,
        recipient_type: userType === 'worker' ? 'customer' : 'worker',
        message: newMessage.trim()
      };
      
      await axios.post(`${API}/messages`, messageData);
      setNewMessage('');
      fetchMessages(activeConversation.id);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!user?.id || !isOpen) return;

    const wsUrl = `${process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://')}/ws/chat/${user.id}`;
    
    try {
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message' && activeConversation?.id === data.conversation_id) {
          fetchMessages(activeConversation.id);
        } else {
          fetchConversations();
        }
      };
      
      wsRef.current.onerror = () => {
        console.log('WebSocket connection failed, using polling');
      };
    } catch (error) {
      console.log('WebSocket not available, using polling');
    }

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isOpen, activeConversation?.id]);

  // Poll for new messages
  useEffect(() => {
    if (!isOpen || !user?.id) return;
    
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);
    
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user?.id]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Select conversation
  const selectConversation = (conv) => {
    setActiveConversation(conv);
    fetchMessages(conv.id);
  };

  if (!user) return null;

  return (
    <>
      {/* Chat Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 md:bottom-6 md:right-6 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 md:p-4 rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        data-testid="chat-toggle-btn"
      >
        {isOpen ? (
          <X className="w-5 h-5 md:w-6 md:h-6" />
        ) : (
          <div className="relative">
            <MessageCircle className="w-5 h-5 md:w-6 md:h-6" />
            {unreadCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </div>
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-20 md:bottom-24 right-2 md:right-6 z-50 w-[calc(100vw-1rem)] md:w-96 h-[70vh] md:h-[500px] max-h-[600px] bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col overflow-hidden"
          data-testid="chat-window"
        >
          {/* Header */}
          <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold">Messages</span>
            </div>
            {activeConversation && (
              <button 
                onClick={() => setActiveConversation(null)}
                className="text-sm bg-blue-500 hover:bg-blue-400 px-3 py-1 rounded-full"
              >
                Back
              </button>
            )}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden">
            {!activeConversation ? (
              // Conversation List
              <div className="h-full overflow-y-auto">
                {conversations.length === 0 ? (
                  <div className="p-6 text-center text-gray-500">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">No conversations yet</p>
                    <p className="text-sm mt-1">Messages from leads will appear here</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {conversations.map((conv) => {
                      const unread = userType === 'worker' ? conv.unread_worker : conv.unread_customer;
                      const otherName = userType === 'worker' ? conv.customer_name : (conv.worker_name || 'Contractor');
                      
                      return (
                        <button
                          key={conv.id}
                          onClick={() => selectConversation(conv)}
                          className="w-full p-4 text-left hover:bg-gray-50 transition-colors flex items-start gap-3"
                          data-testid={`conversation-${conv.id}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            {userType === 'worker' ? (
                              <User className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Briefcase className="w-5 h-5 text-blue-600" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <span className="font-medium text-gray-900 truncate">{otherName}</span>
                              {unread > 0 && (
                                <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                                  {unread}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 truncate mt-0.5">
                              {conv.last_message || 'No messages yet'}
                            </p>
                            {conv.last_message_at && (
                              <p className="text-xs text-gray-400 mt-1">
                                {new Date(conv.last_message_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              // Message Thread
              <div className="h-full flex flex-col">
                {/* Conversation Header */}
                <div className="p-3 bg-gray-50 border-b">
                  <p className="font-medium text-gray-900">
                    {userType === 'worker' ? activeConversation.customer_name : (activeConversation.worker_name || 'Contractor')}
                  </p>
                  {activeConversation.job_title && (
                    <p className="text-xs text-gray-500">Re: {activeConversation.job_title}</p>
                  )}
                </div>
                
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {loading ? (
                    <div className="flex items-center justify-center h-full">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-gray-500 py-8">
                      <p>No messages yet</p>
                      <p className="text-sm">Start the conversation!</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isOwn = msg.sender_type === userType;
                      return (
                        <div
                          key={msg.id}
                          className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] p-3 rounded-2xl ${
                              isOwn
                                ? 'bg-blue-600 text-white rounded-br-md'
                                : 'bg-gray-100 text-gray-900 rounded-bl-md'
                            }`}
                          >
                            <p className="text-sm break-words">{msg.message}</p>
                            <p className={`text-xs mt-1 ${isOwn ? 'text-blue-100' : 'text-gray-400'}`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-3 border-t bg-white">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      className="flex-1"
                      disabled={sending}
                      data-testid="message-input"
                    />
                    <Button 
                      type="submit" 
                      disabled={!newMessage.trim() || sending}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="send-message-btn"
                    >
                      {sending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default LiveChatWidget;
